/**
 * One source: its Source Profile, its Credential Set, and its Run history.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  SPORTS,
  api,
  applicableParameters,
  emptyFeedGroups,
  feedInputFrom,
  paginationOf,
  type CredentialType,
  type FeedInput,
  type Pagination,
  type RepeatedIdentity,
  type SourceProfile,
  type StatEntry,
  type TemplateValues,
} from "../api";
import { ClientIdentityField } from "../components/ClientIdentityField";
import { RepeatedIdentityField } from "../components/FieldMapping";
import { Layout } from "../components/Layout";
import {
  DEFAULT_PAGINATION,
  PaginationChoiceCompact,
  describePagination,
} from "../components/PaginationChoice";
import {
  Badge,
  Banner,
  Button,
  Card,
  Code,
  Field,
  FieldSet,
  IconButton,
  KeyValue,
  Muted,
  ScrollPanel,
  SectionTitle,
  Select,
  Spinner,
  StatusBadge,
  TableScroll,
  TextArea,
  TextInput,
} from "../components/ui";
import {
  EyeIcon,
  KeyIcon,
  PencilIcon,
  PlayIcon,
  PlusIcon,
  TrashIcon,
} from "../components/icons";
import { SchedulePanel } from "../components/SchedulePanel";
import { StatisticMenu } from "../components/StatisticMenu";
import { statOptions } from "../stat-search";
import { composedSourceName, matchesComposed } from "../source-name";
import { SHOW_SCHEDULING } from "../features";
import { TemplateParameters, describeTemplate } from "../components/TemplateParameters";
import { POLL_LIST_MS, keys } from "../queries";
import { formatDuration, formatRelative, formatTimestamp, humaniseCode } from "../format";

/**
 * What started a Run. `scheduled` is the one that means nobody did — it is
 * badged differently for exactly that reason.
 */
const TRIGGER_LABELS: Record<string, string> = {
  manual: "Manual",
  test: "Test",
  scheduled: "Scheduled",
};

export function SourceDetail() {
  const { profileId = "" } = useParams();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // The profile and its Runs move together — a Run finishing changes both — so
  // they are fetched as a pair and polled on the same clock.
  const [profileQuery, runsQuery] = useQueries({
    queries: [
      {
        queryKey: keys.profile(profileId),
        queryFn: () => api.readProfile(profileId),
        refetchInterval: POLL_LIST_MS,
      },
      {
        queryKey: keys.profileRuns(profileId),
        queryFn: () => api.listRuns(profileId),
        refetchInterval: POLL_LIST_MS,
      },
    ],
  });

  const profile = profileQuery.data ?? null;
  const runs = runsQuery.data ?? [];

  /**
   * Everything under this profile is stale at once.
   *
   * `keys.profile(id)` is a prefix of `keys.profileRuns(id)`, so a single
   * invalidation reaches both — which is why the panels below can ask for a
   * refresh without knowing what else is on the screen.
   */
  const refresh = () => queryClient.invalidateQueries({ queryKey: keys.profile(profileId) });

  const startRun = useMutation({
    mutationFn: (testMode: boolean) => api.startRun(profileId, testMode),
    onSuccess: (run) => navigate(`/runs/${run.id}`),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteProfile(profileId),
    /**
     * The Sources list is already in the cache and still fresh, so navigating
     * back to it would re-render the row for the source that was just deleted
     * until `staleTime` expired. Dropping the profile out of the cached list
     * makes the list correct on arrival; invalidating it is what makes the
     * server agree. Its own entries go, because nothing can refetch a profile
     * the server no longer has.
     */
    onSuccess: async () => {
      // Any list request already in flight was sent before this delete landed,
      // so its answer still names this source — and it would resolve into the
      // cache after the line below had taken it out, putting the row back on
      // the list this screen is about to navigate to.
      await queryClient.cancelQueries({ queryKey: keys.profiles });
      queryClient.setQueryData<SourceProfile[]>(keys.profiles, (list) =>
        list?.filter((entry) => entry.id !== profileId),
      );
      queryClient.removeQueries({ queryKey: keys.profile(profileId) });
      queryClient.invalidateQueries({ queryKey: keys.profiles });
      navigate("/sources");
    },
  });

  // Panels below report their own failures through this, so one Banner at the
  // top of the screen covers both what the screen fetched and what it wrote.
  const [panelError, setPanelError] = useState<string | null>(null);

  const error =
    messageFor(profileQuery.error ?? runsQuery.error, "Could not load this source.") ??
    messageFor(startRun.error, "Could not start that Run.") ??
    messageFor(remove.error, "Could not delete this source.") ??
    panelError;

  if (!profile) {
    return (
      <Layout breadcrumbs={[{ label: "Sources", to: "/sources" }]}>
        {error ? <Banner tone="danger" title={error} /> : <Spinner label="Loading source" />}
      </Layout>
    );
  }

  const inFlight = runs.some((run) => run.status === "running" || run.status === "queued");

  return (
    <Layout
      breadcrumbs={[{ label: "Sources", to: "/sources" }, { label: profile.name }]}
      title={profile.name}
      description={`${profile.league} · ${profile.season} season · ${profile.feeds.length} feed${
        profile.feeds.length === 1 ? "" : "s"
      }`}
      actions={
        <>
          <Button onClick={() => startRun.mutate(true)} disabled={inFlight} title="Runs everything, writes nothing.">
            Test run
          </Button>
          <Button
            variant="primary"
            icon={inFlight ? undefined : <PlayIcon size={16} />}
            onClick={() => startRun.mutate(false)}
            disabled={inFlight}
            title={inFlight ? "This source already has a Run in progress." : undefined}
          >
            {inFlight ? "Running…" : "Scrape"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-lg">
        {error && <Banner tone="danger" title={error} />}
        {notice && <Banner tone="success" title={notice} />}

        <div className="grid grid-cols-1 gap-lg fold:grid-cols-2">
          <Card
            title="Source Profile"
            actions={
              <>
                {/*
                  This card's two edits, named for what each one opens.

                  *Edit source* is the settings on the card below it — the
                  League, the season, the Identity Key, the Feed addresses.
                  *Edit fields* is the other screen, where six hundred paths are
                  bound to statistics. They were *Edit* and *Review fields*,
                  which left the more consequential of the two sounding like a
                  read-only glance and the other sounding like the whole source.

                  Outlined first, solid second, on the pattern the page header
                  already sets with *Test run* and *Scrape*: the emphasis is on
                  the field mapping, because that is the work this screen exists
                  to start and the work somebody comes back to.

                  Both are the same `btn` height, which is what the `ghost`
                  variant they started as is not — `h-auto` and tighter padding
                  made a borderless grey label beside a bordered one read as two
                  unrelated things rather than as one card's two actions.
                */}
                <Button
                  variant="secondary"
                  size="sm"
                  icon={editing ? undefined : <PencilIcon size={14} />}
                  onClick={() => setEditing((value) => !value)}
                >
                  {editing ? "Cancel" : "Edit source"}
                </Button>
                {/*
                  Hidden while the profile editor is open: it navigates away,
                  and a button that silently discards somebody's half-finished
                  edit is worse than one they have to close a form to reach.
                  That leaves *Cancel* alone in the header, outlined, which is
                  the right weight for the action that does nothing.
                */}
                {!editing && (
                  <Button
                    variant="primary"
                    size="sm"
                    icon={<EyeIcon size={14} />}
                    onClick={() => navigate(`/sources/${profile.id}/fields`)}
                  >
                    Edit fields
                  </Button>
                )}
              </>
            }
          >
            {editing ? (
              <ProfileEditor
                profile={profile}
                onSaved={(updated) => {
                  queryClient.setQueryData(keys.profile(profileId), updated);
                  setEditing(false);
                  setNotice("Source Profile saved.");
                }}
              />
            ) : (
              <div className="flex flex-col gap-md">
                <div className="grid grid-cols-1 gap-md fold:grid-cols-2">
                  <KeyValue label="League">{profile.league}</KeyValue>
                  <KeyValue label="Sport">{profile.sport}</KeyValue>
                  <KeyValue label="Source">
                    {profile.source_slug ? (
                      <Code>{profile.source_slug}</Code>
                    ) : (
                      <Muted>unrecorded</Muted>
                    )}
                  </KeyValue>
                  <KeyValue label="Season">
                    <div className="flex items-center gap-xs">
                      {profile.season}
                      {profile.season_active ? <Badge tone="success">active</Badge> : <Badge>closed</Badge>}
                    </div>
                  </KeyValue>
                </div>

                <KeyValue label="Identity Key">
                  <div className="flex flex-wrap items-center gap-xxs">
                    {profile.identity_key.map((part) => (
                      <Badge key={part} tone="accent">
                        <Code className="code-sm">{part}</Code>
                      </Badge>
                    ))}
                    {/* Only when it is `merge`, which is the whole of what this
                        badge is for. `fail` is the default every profile has
                        and saying so on every source would be a row of noise;
                        a profile that folds repeated rows is doing something
                        to its own data, and a reader of this panel should not
                        have to open the form to find out. The Run page reports
                        the fold nowhere, so this is the only place it shows. */}
                    {profile.repeated_identity === "merge" && (
                      <span title="A row repeating this key inside one Feed is folded into the first copy rather than failing the Run.">
                        <Badge tone="warning">repeats folded</Badge>
                      </span>
                    )}
                  </div>
                </KeyValue>

                {/*
                  Beside the Identity Key, and above the Feeds panel rather
                  than below it. Both of these are facts about the field
                  mapping, so they read together — and the Feeds panel is a
                  scrolling box tall enough that anything after it is something
                  a reader has to go looking for. That is where this used to be.
                  The way to change it is the button in the header.
                */}
                <KeyValue label="Fields classified">
                  <div className="flex flex-wrap items-center gap-xs">
                    <span>{profile.binding_count} bound</span>
                    {profile.unreviewed_binding_count > 0 && (
                      <Badge tone="warning">{profile.unreviewed_binding_count} still to review</Badge>
                    )}
                  </div>
                </KeyValue>

                {profile.scope && <KeyValue label="Stat group">{profile.scope}</KeyValue>}
                {profile.record_path && (
                  <KeyValue label="Record Path">{profile.record_path}</KeyValue>
                )}

                <KeyValue label={`Feeds (${profile.feeds.length})`}>
                  {/* **What the last read actually found, never what anything
                      declared.** The template used to carry a `measured empty`
                      flag on four of Wyscout's conferences, which unticked them
                      and said "it would fetch no players" under the checkbox
                      before anything was fetched; a live probe six weeks later
                      found the Pac-12 answering 148 men and holding a women's
                      competition the same note said did not exist, so the
                      default profiles were composing without 348 players while
                      the screen said there were none
                      (`add-source-usability/04`). The claim now lives here,
                      after the fetch, with the date it was true on — and a Feed
                      nobody has read says nothing at all. */}
                  <EmptyFeedNotice feeds={profile.feeds} />
                  {/* Counted in the label because the panel below crops the
                      list: without the number, "there is more in here" is
                      something a reader has to discover by scrolling. */}
                  <ScrollPanel>
                    <div className="flex flex-col gap-xxs">
                      {profile.feeds.map((feed) => {
                        // Only a Feed that has been given a mode says anything
                        // here. Printing "automatic" against every other one
                        // would bury the handful that were declared
                        // deliberately in a column of noise.
                        const paging = describePagination(paginationOf(feed));
                        return (
                          <div key={feed.id} className="flex flex-wrap items-baseline gap-xs">
                            <Code>{feed.url}</Code>
                            {feed.label && <Muted>{feed.label}</Muted>}
                            {paging && <Badge tone="accent">{paging}</Badge>}
                          </div>
                        );
                      })}
                    </div>
                  </ScrollPanel>
                </KeyValue>
              </div>
            )}
          </Card>

          <CredentialPanel profile={profile} onChanged={refresh} onNotice={setNotice} onError={setPanelError} />
        </div>

        {profile.template_key && (
          <TemplatePanel profile={profile} onSaved={refresh} onNotice={setNotice} onError={setPanelError} />
        )}

        {/* Reports its own outcome inline rather than through this screen's
            Banner: the panel sits well below the fold, and a message at the top
            of the page is not an answer to a button pressed down here. */}
        {SHOW_SCHEDULING && <SchedulePanel profile={profile} onChanged={refresh} />}

        <Card title="Run history" description={`${runs.length} recorded`} flush>
          {runs.length === 0 ? (
            <div className="p-lg">
              <Muted>This source has never run. Press Scrape to pull fresh data.</Muted>
            </div>
          ) : (
            <TableScroll stickyHeader>
              <table className="table">
                <thead>
                  <tr>
                    <th>Started</th>
                    <th>Status</th>
                    <th>Trigger</th>
                    <th>Duration</th>
                    <th className="text-right">Players</th>
                    <th>Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => (
                    <tr key={run.id}>
                      <td>
                        <Link to={`/runs/${run.id}`} className="text-body-sm-medium text-ink">
                          {formatRelative(run.created_at)}
                        </Link>
                      </td>
                      <td>
                        <StatusBadge status={run.status} />
                      </td>
                      <td>
                        <Badge tone={run.trigger === "scheduled" ? "accent" : undefined}>
                          {TRIGGER_LABELS[run.trigger] ?? capitalise(run.trigger)}
                        </Badge>
                      </td>
                      <td>
                        <Muted>{formatDuration(run.duration_ms)}</Muted>
                      </td>
                      <td className="text-right">
                        {run.player_count?.toLocaleString() ?? <Muted>—</Muted>}
                      </td>
                      <td>
                        {run.failure_code ? (
                          <span className="text-body-sm text-danger-ink">
                            {humaniseCode(run.failure_code)}
                          </span>
                        ) : (
                          <Muted>{formatTimestamp(run.finished_at)}</Muted>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </Card>

        <Card
          title="Delete this source"
          description="Removes the Source Profile, its Bindings, its Credential Set and every Run it has recorded."
          actions={
            !confirmingDelete && (
              <Button variant="danger" icon={<TrashIcon size={14} />} onClick={() => setConfirmingDelete(true)}>
                Delete
              </Button>
            )
          }
        >
          {confirmingDelete && (
            <Banner tone="danger" title={`Delete ${profile.name}? This cannot be undone.`}>
              <div className="flex items-center gap-xs">
                <Button variant="danger" size="sm" onClick={() => remove.mutate()}>
                  Yes, delete it
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirmingDelete(false)}>
                  Keep it
                </Button>
              </div>
            </Banner>
          )}
        </Card>
      </div>
    </Layout>
  );
}

/**
 * Changing what a template was given, and recomposing every Feed from it.
 *
 * This is the edit a Source Template exists to make cheap: rolling PFF over to
 * the next season rewrites thirteen addresses, and the alternative is retyping
 * four parameters or thirteen URLs. It is safe to do because a Feed keeps its
 * identity by its **name** first and its URL second — so the thirteen reports
 * survive their addresses being rewritten, and every Binding scoped to
 * *Passing* still reads the passing report (see `_match_existing_feeds`).
 *
 * The season is saved onto the profile alongside the parameters, because it is
 * both: a parameter of the address and the number in the published filename.
 * The two disagreeing would publish a 2024 file from 2025 data.
 */
function TemplatePanel({
  profile,
  onSaved,
  onNotice,
  onError,
}: {
  profile: SourceProfile;
  onSaved: () => void;
  onNotice: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState<TemplateValues>(profile.template_parameters);

  const templates = useQuery({ queryKey: keys.templates, queryFn: api.listTemplates });
  const template = templates.data?.find((entry) => entry.key === profile.template_key);

  const composition = useQuery({
    queryKey: keys.templateComposition(profile.template_key ?? "", values),
    queryFn: () => api.composeTemplate(profile.template_key ?? "", values),
    enabled: editing && Boolean(profile.template_key),
    retry: false,
  });

  /**
   * What this profile's *saved* parameters compose, as opposed to the edited ones.
   *
   * The baseline the two questions below are asked against: a name and a League
   * that still match what the saved parameters compose were never customised,
   * so a recompose may carry them. Its own query rather than a derivation,
   * because only the server can answer what a set of parameters composes — and
   * it costs nothing extra when the panel opens, since `values` starts equal to
   * `profile.template_parameters` and the two queries share a key until the
   * first edit.
   */
  const baseline = useQuery({
    queryKey: keys.templateComposition(
      profile.template_key ?? "",
      profile.template_parameters,
    ),
    queryFn: () =>
      api.composeTemplate(profile.template_key ?? "", profile.template_parameters),
    enabled: editing && Boolean(profile.template_key),
    retry: false,
  });

  const save = useMutation({
    mutationFn: async () => {
      const composed = composition.data;
      if (!composed) throw new ApiError(0, "not_composed", "Nothing has been composed yet.");
      const before = baseline.data?.parameters;
      /**
       * The name and the League follow the parameters — but only the ones
       * nobody has overridden.
       *
       * **Changing a division here used to rewrite thirteen addresses and
       * leave both standing**, which is how the Sources list came to show a
       * source called *PFF — Player Statistics (FBS, Reg + Post)* publishing
       * under `fcs`. The League is the worse half of the two: it is the name
       * of the file every Run writes, so leaving it behind publishes one
       * division's players under another division's filename, and nothing on
       * the row says so.
       *
       * The test is against what the *saved* parameters compose, never against
       * the new ones: a League somebody typed by hand — `CFB - 3C2A` over a
       * template's own slug — and a name somebody wrote are decisions, and a
       * recompose that discarded them would be this bug with the sign
       * reversed. Where the baseline cannot be read, both are left alone.
       */
      const league =
        before !== undefined &&
        profile.league === String(baseline.data?.defaults.league ?? "") &&
        composed.defaults.league !== undefined
          ? { league: String(composed.defaults.league) }
          : {};
      const name =
        before !== undefined &&
        template &&
        matchesComposed(
          profile.name,
          template.name,
          applicableParameters(template, before),
          before,
        )
          ? {
              name: composedSourceName(
                template.name,
                applicableParameters(template, composed.parameters),
                composed.parameters,
              ),
            }
          : {};
      return api.updateProfile(profile.id, {
        ...name,
        ...league,
        season: Number(composed.defaults.season ?? profile.season),
        template_parameters: composed.parameters,
        // Whole, not field by field — see `feedInputFrom`. This is the season
        // rollover, and it re-sends every Feed on the profile to change one
        // number, so a property it failed to carry across is a property it
        // destroys.
        feeds: composed.feeds.map(feedInputFrom),
      });
    },
    onSuccess: (updated) => {
      const renamed = updated.name !== profile.name;
      const releagued = updated.league !== profile.league;
      setValues(updated.template_parameters);
      setEditing(false);
      onNotice(
        `Recomposed ${updated.feeds.length} Feed${updated.feeds.length === 1 ? "" : "s"}.` +
          // Named rather than left to be noticed, because both change what the
          // Sources list and the published filename say about this source, and
          // a change nobody was told about is one nobody can object to.
          (releagued ? ` The League is now ${updated.league}.` : "") +
          (renamed ? ` It is now called "${updated.name}".` : "") +
          " The field review is unchanged.",
      );
      onSaved();
    },
    onError: (error) =>
      onError(error instanceof ApiError ? error.message : "Could not recompose the Feeds."),
  });

  const composeError = composition.error instanceof ApiError ? composition.error : null;

  return (
    <Card
      title="Source Template"
      description={template ? `${template.name} · ${template.publisher}` : profile.template_key ?? ""}
      actions={
        // Matched to the Source Profile card's *Edit source*, because it is the
        // same kind of action one card further down — this card's own settings,
        // not a different screen. It follows that button's weight rather than
        // keeping one of its own, so the two never disagree about what editing
        // a card looks like.
        <Button
          variant="secondary"
          size="sm"
          icon={editing ? undefined : <PencilIcon size={14} />}
          onClick={() => {
            setValues(profile.template_parameters);
            setEditing((value) => !value);
          }}
        >
          {editing ? "Cancel" : "Change"}
        </Button>
      }
    >
      {!editing ? (
        <div className="flex flex-col gap-xs">
          <Muted>{describeTemplate(template, profile.template_parameters)}</Muted>
          <Muted>
            These Feeds were composed rather than typed. Changing a value here rewrites every
            address and leaves the field review alone.
          </Muted>
        </div>
      ) : templates.isPending ? (
        <Spinner label="Reading the template" />
      ) : !template ? (
        <Banner tone="warning" title="This template is no longer in the source menu">
          Its Feeds still run exactly as they are. Editing them is the Source Profile panel
          above.
        </Banner>
      ) : (
        <div className="flex flex-col gap-md">
          <TemplateParameters
            template={template}
            values={values}
            onChange={setValues}
            invalid={composeError?.field}
            message={composeError?.message}
          />
          {composition.data && (
            <Muted>
              {composition.data.feeds.length} Feed
              {composition.data.feeds.length === 1 ? "" : "s"} composed
              {composition.isFetching ? ", recomposing…" : ""}
            </Muted>
          )}
          <div className="flex items-center gap-xs">
            <Button
              variant="primary"
              size="sm"
              disabled={!composition.data || composition.isFetching || save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? "Recomposing…" : "Recompose the Feeds"}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function ProfileEditor({
  profile,
  onSaved,
}: {
  profile: SourceProfile;
  onSaved: (profile: SourceProfile) => void;
}) {
  const [name, setName] = useState(profile.name);
  const [league, setLeague] = useState(profile.league);
  const [sport, setSport] = useState(profile.sport);
  const [season, setSeason] = useState(profile.season);
  const [seasonActive, setSeasonActive] = useState(profile.season_active);
  const [identityKey, setIdentityKey] = useState(profile.identity_key.join(", "));
  const [repeatedIdentity, setRepeatedIdentity] = useState<RepeatedIdentity>(
    profile.repeated_identity,
  );
  const [feeds, setFeeds] = useState<FeedDraft[]>(() => profile.feeds.map(toDraft));
  const [recordPath, setRecordPath] = useState(profile.record_path ?? "");
  const [eligibilityStat, setEligibilityStat] = useState(profile.eligibility_stat_key);
  const [eligibilityMin, setEligibilityMin] = useState(profile.eligibility_min_value);
  const [minBoundStats, setMinBoundStats] = useState(profile.min_bound_stats);
  const [requiredColumns, setRequiredColumns] = useState(profile.required_columns.join(", "));
  const [minPlayers, setMinPlayers] = useState(profile.min_players);
  const [maxPlayers, setMaxPlayers] = useState(profile.max_players);
  const [sourceSlug, setSourceSlug] = useState(profile.source_slug ?? "");
  const [requestBudget, setRequestBudget] = useState(profile.request_budget);
  const [requestDelay, setRequestDelay] = useState(profile.request_delay_ms);
  const [userAgent, setUserAgent] = useState(profile.user_agent ?? "");
  const [busy, setBusy] = useState(false);

  /**
   * Why the last Save was refused, shown against the button rather than
   * through the screen's Banner.
   *
   * The refusal that prompted this is the output-key collision, which arrives
   * when somebody edits the League or the season — fields at the top of a form
   * whose Save sits below the Feed rows and the request settings. Answering a
   * press down here with a line above the fold reads as the button doing
   * nothing at all.
   */
  const [saveError, setSaveError] = useState<string | null>(null);
  const errorAnchor = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!saveError) return;
    const calm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    errorAnchor.current?.scrollIntoView({
      behavior: calm ? "auto" : "smooth",
      block: "center",
    });
    errorAnchor.current?.focus({ preventScroll: true });
  }, [saveError]);

  // The participation rule names a statistic, so the choices come from the
  // Stat Dictionary rather than being typed from memory. Shared with the
  // dictionary screen through the cache, so opening this form after visiting
  // that one costs no request at all.
  //
  // Loaded unfiltered and scoped below rather than requested for one sport.
  // Sport is an editable control on this very form, so a scoped request would
  // refetch on every change of it and hand this select a loading state halfway
  // through editing — and it would stop sharing `keys.stats` with the Stat
  // Dictionary screen, which is what makes this form cost no request at all.
  const { data: stats = [] } = useQuery({ queryKey: keys.stats, queryFn: () => api.listStats() });

  /**
   * The statistics this profile's sport actually has.
   *
   * The select offered every sport's entries until now, whatever the profile
   * was — 481 options on a baseball profile, of which the number a baseball Run
   * could resolve is nought. That is the hazard issue 03 closed on the two
   * field-mapping screens, on a third screen with a different control, and this
   * key decides who is ranked at all rather than how one column is scored: a
   * Run resolves the rule by the profile's sport and the key together, and
   * `_is_eligible` reads a player missing the statistic as one who did not
   * play. A cross-sport choice therefore excludes every player, not some.
   *
   * It is caught rather than published — `ranked_statistics_bound` is a gate
   * check and fails saying every player reads as not having played — so what
   * this saves is a Run, not a wrong file.
   *
   * Scoped to the `sport` **being edited**, not to `profile.sport`. The Sport
   * control is a few fields above this one, so the two move together or this
   * select spends the rest of the edit offering the sport the profile is
   * leaving.
   */
  const sportEntries = useMemo(
    () => stats.filter((entry) => entry.sport === sport),
    [stats, sport],
  );

  /**
   * The chosen statistic when this sport has no entry under that key, resolved
   * wherever it does exist so the option can name it.
   *
   * Keys are unique per sport and not globally (ADR-0017), so a key filtered
   * out of `sportEntries` may be several sports' — `penalties` is both
   * football's and soccer's. Which one was meant is not recorded and cannot be
   * recovered from a bare string, so the first match names the option and the
   * option says which sport it came from: a guess presented as one.
   */
  const outOfSport = useMemo((): StatEntry | null => {
    if (eligibilityStat === null) return null;
    if (sportEntries.some((entry) => entry.key === eligibilityStat)) return null;
    return stats.find((entry) => entry.key === eligibilityStat) ?? null;
  }, [stats, sportEntries, eligibilityStat]);

  /**
   * The rows the Participation statistic menu offers: this sport's entries, and
   * the saved rule itself whenever they do not hold it.
   *
   * **A saved rule is always offered, whatever `sportEntries` holds**, and that
   * is what `statOptions` puts first. Scoping a menu under a value already
   * stored is how issue 01 is reintroduced while fixing 03: the value stops
   * being among the options and a `<select>` falls to its first one, which here
   * read *No rule, rank everyone* over a profile that has a rule. That is not a
   * cosmetic lie — saving this form writes `eligibility_stat_key` back, so the
   * next Save would drop the rule the screen claimed was already absent. The
   * control is no longer a `<select>` and so can no longer fall to anything, but
   * the row is still offered: it is how the rule is read, and how it is kept.
   *
   * Not conditioned on `outOfSport`: a stored key can fail to be offered without
   * belonging to another sport. `eligibility_stat_key` is a plain string rather
   * than a foreign key, so deleting a dictionary entry leaves every profile
   * naming it dangling with no cascade to notice.
   *
   * **`"threshold"`, which is what the detail panel under the menu reads.** A
   * choice made here is compared against the Minimum beside it and never
   * ranked: `_is_eligible` is `value >= minimum` and reads no Direction, no
   * Distribution and no per-90 derivation. The panel on the field-mapping
   * screens leads with Direction because that is what the ranker consumes;
   * leading with it here would describe machinery that does not run on this
   * decision. It changes the consequence of an unresolvable key too — a ranked
   * column publishes under the raw key, where a participation rule the sport
   * does not hold leaves every player with no value and excludes all of them,
   * which is the claim the comment on the menu below already makes.
   */
  const eligibilityOptions = useMemo(
    () => statOptions(sportEntries, eligibilityStat, outOfSport, "threshold"),
    [sportEntries, eligibilityStat, outOfSport],
  );

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    // Dropped before the attempt rather than after it, so a second press
    // against the same refusal re-runs the travel below.
    setSaveError(null);
    try {
      onSaved(
        await api.updateProfile(profile.id, {
          name,
          league,
          sport,
          season,
          season_active: seasonActive,
          identity_key: identityKey.split(",").map((part) => part.trim()).filter(Boolean),
          repeated_identity: repeatedIdentity,
          feeds: feeds
            .filter((feed) => feed.url.trim() !== "")
            .map(
              (feed): FeedInput => ({
                url: feed.url.trim(),
                // Carried, not shown as an editable field: a Binding may be
                // scoped to a Feed by label, and a label dropped on the way
                // through this form would take those Bindings with it.
                label: feed.label,
                pagination: feed.pagination,
              }),
            ),
          record_path: recordPath.trim() || null,
          eligibility_stat_key: eligibilityStat,
          eligibility_min_value: eligibilityMin,
          min_bound_stats: minBoundStats,
          required_columns: requiredColumns
            .split(",")
            .map((part) => part.trim())
            .filter(Boolean),
          min_players: minPlayers,
          max_players: maxPlayers,
          source_slug: sourceSlug.trim() || null,
          request_budget: requestBudget,
          request_delay_ms: requestDelay,
          // Blank means the portal's honest default; the backend normalises it
          // to null so an empty field cannot strip the header entirely.
          user_agent: userAgent.trim() || null,
        }),
      );
    } catch (caught) {
      setSaveError(caught instanceof ApiError ? caught.message : "Could not save.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={save}>
      <div className="flex flex-col gap-md">
        <Field label="Name">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="grid grid-cols-1 gap-md fold:grid-cols-2">
          <Field label="League">
            <TextInput value={league} onChange={(e) => setLeague(e.target.value)} />
          </Field>
          <Field label="Sport" hint="The folder published files land in. Gender belongs in the league name.">
            <Select value={sport} onChange={(e) => setSport(e.target.value)}>
              {/*
                A saved sport the standard no longer allows stays visible and
                selected. Without it the select would fall to its first option
                and the next save would quietly move the league's files to a
                different sport folder — the opposite of showing someone that
                their profile cannot publish.
              */}
              {!SPORTS.includes(sport as (typeof SPORTS)[number]) && (
                <option value={sport}>{sport} (not a valid sport folder)</option>
              )}
              {SPORTS.map((option) => (
                <option key={option} value={option}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Season end year">
            <TextInput type="number" value={season} onChange={(e) => setSeason(Number(e.target.value))} />
          </Field>
        </div>
        <Field label="Season status" hint="A closed season stops unchanged data being reported as stale.">
          <Select
            value={seasonActive ? "active" : "closed"}
            onChange={(e) => setSeasonActive(e.target.value === "active")}
          >
            <option value="active">Active</option>
            <option value="closed">Closed</option>
          </Select>
        </Field>
        <Field label="Identity Key" hint="Comma separated. Two fields are usually needed, because a name alone collides.">
          <TextInput value={identityKey} onChange={(e) => setIdentityKey(e.target.value)} />
        </Field>
        {/* Directly under the key it is about. Widening the key and declaring
            the repeats harmless are the two answers to one collision, and they
            are not interchangeable: a key that is genuinely too narrow should
            be widened, and only a publisher measured repeating whole rows
            should be merged. Put a screen apart, the easy one gets reached for
            first. */}
        <RepeatedIdentityField value={repeatedIdentity} onChange={setRepeatedIdentity} />
        <FeedList feeds={feeds} onChange={setFeeds} />
        <Field
          label="Record Path"
          hint="Leave blank and the player list is found by shape. Name it where a source publishes something longer beside its players: a glossary of stat abbreviations, say, or a list of team logos."
        >
          <TextInput
            value={recordPath}
            placeholder="Found by shape"
            onChange={(e) => setRecordPath(e.target.value)}
          />
        </Field>

        <div>
          <SectionTitle>Who gets ranked</SectionTitle>
          <div className="flex flex-col gap-md">
            <div className="grid grid-cols-1 gap-md fold:grid-cols-[2fr_1fr]">
              <Field
                label="Participation statistic"
                hint="Players below the minimum leave the output and the comparison group. Without this, someone who never played gets ranked, and that shifts everyone else's percentile."
              >
                {/* Searchable, for the same reason the field-mapping cells
                    are: this is the whole of one sport's dictionary, 392 rows
                    on a football profile. The out-of-sport row `statOptions`
                    puts first says what it is — a Run resolves this rule by the
                    profile's sport and this key together, so leaving it chosen
                    excludes every player. */}
                <StatisticMenu
                  value={eligibilityStat}
                  options={eligibilityOptions}
                  label="Participation statistic"
                  placeholder="No rule, rank everyone"
                  clearLabel="No rule, rank everyone"
                  compact={false}
                  onChange={setEligibilityStat}
                />
              </Field>
              <Field label="Minimum">
                <TextInput
                  type="number"
                  value={eligibilityMin}
                  disabled={!eligibilityStat}
                  onChange={(e) => setEligibilityMin(Number(e.target.value))}
                />
              </Field>
            </div>
            <Field
              label="Minimum statistics to be scored"
              hint="A player with fewer bound statistics than this appears in the file but takes no Impact Factor."
            >
              <TextInput
                type="number"
                min={1}
                value={minBoundStats}
                onChange={(e) => setMinBoundStats(Number(e.target.value))}
              />
            </Field>
          </div>
        </div>

        <div>
          <SectionTitle>What a trustworthy answer looks like</SectionTitle>
          <div className="flex flex-col gap-md">
            <Field
              label="Fields that must be present"
              hint="Comma separated, and written the same way a binding path is: relative to the block above, if this source has one. Some sources sell columns rather than access: they answer normally to an expired sign-in, with the right number of players and fewer fields. Naming the fields you are paying for is the only thing that catches it."
            >
              <TextInput
                value={requiredColumns}
                placeholder="grades_offense, grades_pass"
                onChange={(e) => setRequiredColumns(e.target.value)}
              />
            </Field>
            <div className="grid grid-cols-1 gap-md fold:grid-cols-2">
              <Field
                label="Fewest players worth publishing"
                hint="A Run producing fewer players than this fails instead of publishing. A collapsed count is usually a filter selecting the wrong population, which succeeds quietly. Zero means no floor."
              >
                <TextInput
                  type="number"
                  min={0}
                  value={minPlayers}
                  onChange={(e) => setMinPlayers(Number(e.target.value))}
                />
              </Field>
              <Field
                label="Most players worth publishing"
                hint="The other end of the band a schedule is checked against. A filter that widens publishes as quietly as one that collapses. Zero means no ceiling, but a source with neither cannot pass the schedule's player-count check, because there is nothing to check it against."
              >
                <TextInput
                  type="number"
                  min={0}
                  value={maxPlayers}
                  onChange={(e) => setMaxPlayers(Number(e.target.value))}
                />
              </Field>
            </div>
            <Field
              label="Source slug"
              hint="Where this league's data came from, in the naming standard's vocabulary: pff, espn, wyscout, ncaa. Published in the league's Status File so monitoring can tell two pipelines for one league apart. Leave blank if none of them fits."
            >
              <TextInput
                value={sourceSlug}
                placeholder="pff"
                spellCheck={false}
                onChange={(e) => setSourceSlug(e.target.value)}
              />
            </Field>
          </div>
        </div>

        <div>
          <SectionTitle>Fetching</SectionTitle>
          <div className="grid grid-cols-1 gap-md fold:grid-cols-2">
            <Field
              label="Request budget"
              hint="A Run that needs more pages than this stops rather than hammering the source."
            >
              <TextInput
                type="number"
                value={requestBudget}
                onChange={(e) => setRequestBudget(Number(e.target.value))}
              />
            </Field>
            <Field label="Delay between requests (ms)">
              <TextInput
                type="number"
                value={requestDelay}
                onChange={(e) => setRequestDelay(Number(e.target.value))}
              />
            </Field>
          </div>
          <ClientIdentityField value={userAgent} onChange={setUserAgent} />
        </div>

        {saveError && (
          <div ref={errorAnchor} tabIndex={-1}>
            <Banner tone="danger" title={saveError} />
          </div>
        )}

        <div>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </form>
  );
}

/**
 * The Feed list, one row per Feed.
 *
 * This was a textarea of addresses, one per line, which could say nothing about
 * a Feed beyond where it is. A Feed also carries a label and a pagination mode,
 * and a form that could not see either quietly reset both every time somebody
 * renamed a source — so the rows exist because the Feed has more than one
 * property, not because a list of addresses wanted decorating.
 */
interface FeedDraft {
  /** Stable across edits, so removing a row does not renumber the survivors. */
  key: string;
  url: string;
  label: string | null;
  pagination: Pagination;
}

/**
 * The parts of this source whose last read came back with nobody in them.
 *
 * **A reading, not a declaration**, and that distinction is the whole of
 * `add-source-usability/04`. Four of Wyscout's conferences used to carry a
 * `measured empty` flag in the template: it unticked them and rendered *"it
 * would fetch no players"* under the checkbox, before a single request. Probed
 * live six weeks after the reading behind it, the Pac-12 answered **148** men
 * and held a women's competition the same note said did not exist — so the
 * default men's profile was composing without 148 players and the women's
 * without 200, with every check green and the screen saying there was nobody
 * there.
 *
 * So nothing claims a population in advance. A Feed's last read records what it
 * returned, and this says so afterwards, with the date attached — because a
 * population is only ever true on a day, and the previous version's whole
 * failure was reading as though it were permanent.
 *
 * Silent in the two states that are not evidence: a Feed nobody has read, and a
 * conference where some projections returned players. The first would accuse
 * every Feed older than the column; the second is a different fault, and
 * reporting it would send somebody to untick a box that works. `emptyFeedGroups`
 * holds both rules and is tested without a DOM.
 */
function EmptyFeedNotice({ feeds }: { feeds: SourceProfile["feeds"] }) {
  const groups = useMemo(() => emptyFeedGroups(feeds), [feeds]);
  if (groups.length === 0) return null;
  // The newest read across the groups, so one date serves the whole notice
  // rather than repeating on every row.
  const readAt = groups.reduce((latest, group) => (group.readAt > latest ? group.readAt : latest), "");
  return (
    <div className="mb-xs flex flex-col gap-xxs">
      <Muted tone="text-warning-ink">
        {groups.length === 1
          ? "1 of this source's parts returned no players when it was last read"
          : `${groups.length} of this source's parts returned no players when they were last read`}
        {readAt && ` (${formatRelative(readAt)})`}. The addresses answered — there
        was nobody in them. Untick them, or re-read to check whether the
        publisher has filled them since.
      </Muted>
      <div className="flex flex-wrap gap-xxs">
        {groups.map((group) => (
          <Badge key={group.name} tone="warning">
            {group.name}
          </Badge>
        ))}
      </div>
    </div>
  );
}


function toDraft(feed: SourceProfile["feeds"][number]): FeedDraft {
  return { key: feed.id, url: feed.url, label: feed.label, pagination: paginationOf(feed) };
}

function FeedList({
  feeds,
  onChange,
}: {
  feeds: FeedDraft[];
  onChange: (feeds: FeedDraft[]) => void;
}) {
  function replace(key: string, changes: Partial<FeedDraft>) {
    onChange(feeds.map((feed) => (feed.key === key ? { ...feed, ...changes } : feed)));
  }

  return (
    <FieldSet
      label="Feeds"
      hint="Several feeds merge on the Identity Key. Each one says how its own pages are reached."
    >
      {/* Rows are separated by more space than the two lines within a row, so
          which parameter belongs to which address is read by proximity rather
          than by counting. */}
      <div className="flex flex-col gap-md">
        {feeds.map((feed, index) => (
          <div key={feed.key} className="flex flex-col gap-xxs">
            <div className="flex items-center gap-xs">
              <TextInput
                aria-label={`Feed ${index + 1} address`}
                value={feed.url}
                placeholder="https://api.example.org/v1/players?season=2025"
                onChange={(e) => replace(feed.key, { url: e.target.value })}
              />
              <IconButton
                icon={<TrashIcon size={16} />}
                label={`Remove ${feed.url || `feed ${index + 1}`}`}
                onClick={() => onChange(feeds.filter((row) => row.key !== feed.key))}
              />
            </div>
            <div className="flex flex-wrap items-center gap-xs pl-xs">
              {/* The label is shown but not editable here. It is what a
                  Feed-scoped Binding is matched by, so this form's job is to
                  carry it through untouched rather than to offer it up for a
                  casual rename. */}
              {feed.label && <Muted>{feed.label}</Muted>}
              <PaginationChoiceCompact
                value={feed.pagination}
                onChange={(pagination) => replace(feed.key, { pagination })}
                urlLabel={feed.url || `feed ${index + 1}`}
              />
            </div>
          </div>
        ))}
        <div>
          <Button
            size="sm"
            icon={<PlusIcon size={14} />}
            onClick={() => onChange([...feeds, blankFeed(feeds)])}
          >
            Add a feed
          </Button>
        </div>
      </div>
    </FieldSet>
  );
}

/**
 * A new row. Its key only has to be unique among the rows on screen and
 * different from any Feed id, since the API matches a saved Feed by label and
 * URL rather than by anything this form invents.
 */
function blankFeed(feeds: FeedDraft[]): FeedDraft {
  const taken = new Set(feeds.map((feed) => feed.key));
  let key = `new-${feeds.length}`;
  for (let suffix = 1; taken.has(key); suffix += 1) key = `new-${feeds.length}-${suffix}`;
  return { key, url: "", label: null, pagination: DEFAULT_PAGINATION };
}

const CREDENTIAL_TYPES: {
  value: CredentialType;
  label: string;
  fields: string[];
  hint?: string;
}[] = [
  { value: "cookie_jar", label: "Session cookie", fields: ["cookie"] },
  { value: "bearer_token", label: "Bearer token", fields: ["token"] },
  { value: "basic_auth", label: "Username and password", fields: ["username", "password"] },
  {
    value: "login_form",
    label: "Sign in for me",
    fields: ["username", "password", "login_url"],
    hint:
      "The portal signs in with these and keeps the session it is granted, " +
      "renewing it by itself when it lapses. This is the one to pick when the " +
      "alternative is pasting a fresh cookie every time one expires.",
  },
  { value: "static_headers", label: "Custom headers", fields: ["headers"] },
];

/**
 * Field names come from the API's secret shape, which is not always what a
 * person should be shown: `username` is an email address on most sign-in pages,
 * and `login_url` is a page rather than a URL to anyone who does not already
 * know what the portal does with it.
 */
const CREDENTIAL_FIELD_LABELS: Record<string, string> = {
  cookie: "Cookie",
  username: "Email or username",
  login_url: "Sign-in page address",
};

function CredentialPanel({
  profile,
  onChanged,
  onNotice,
  onError,
}: {
  profile: SourceProfile;
  onChanged: () => Promise<void>;
  onNotice: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [type, setType] = useState<CredentialType>("cookie_jar");
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  // Distinct from `busy`, which also covers saving a replacement: only the
  // control that is actually working should be the one that says so.
  const [testing, setTesting] = useState(false);

  /**
   * Whether the portal holds a key for this profile's publisher.
   *
   * Add Source offers the configured key and this screen did not, which left
   * the two halves of one decision on different screens: a profile could be
   * *created* on the portal's key and then never put back on it, because
   * replacing a credential here could only mean pasting one. The reverse was
   * just as lopsided — Add Source had no way to use a key of its own.
   *
   * The same query key as the template panel above, so React Query serves both
   * from one request rather than fetching the catalogue twice.
   */
  const templates = useQuery({ queryKey: keys.templates, queryFn: api.listTemplates });
  const template = templates.data?.find((entry) => entry.key === profile.template_key);
  const portalKeyAvailable =
    template?.credential_type === "bearer_token" &&
    template.credential_preconfigured === true;
  // Only meaningful while the form is open on a bearer token, which is the one
  // type a configured secret can satisfy — a sign-in needs three fields and a
  // cookie jar is nobody's deployment setting.
  const canUsePortalKey = portalKeyAvailable && type === "bearer_token";

  /**
   * Which key, with "nothing chosen yet" distinguished from "chose to paste".
   *
   * A plain boolean defaulted to `false`, which put the control on *Use a
   * different key* and left the Token field on screen — so a portal holding a
   * key opened the form asking for one anyway, which is the thing this control
   * exists to stop. Null means untouched, and untouched takes the portal's key
   * where there is one: it is the reason it was configured.
   *
   * Both halves still travel. `canUsePortalKey` gates the whole thing, so a
   * template with no configured key never shows the control and
   * `usePortalKey` is false — the field is the only route, as before.
   */
  const [keyChoice, setKeyChoice] = useState<"portal" | "own" | null>(null);
  const usePortalKey = canUsePortalKey && keyChoice !== "own";

  const spec = CREDENTIAL_TYPES.find((entry) => entry.value === type)!;

  async function save() {
    setBusy(true);
    try {
      // The portal's own key, resolved server-side from the setting the
      // template names. No secret is sent, because the key was never here to
      // send — see `app/api/credentials.py`.
      if (canUsePortalKey && usePortalKey) {
        await api.saveCredential(profile.id, {
          name: values.name || template!.publisher,
          credential_type: "bearer_token",
          from_template: profile.template_key!,
        });
        setValues({});
        setShowForm(false);
        setKeyChoice(null);
        onNotice("Now using the API key configured on this portal.");
        await onChanged();
        return;
      }
      const secret: Record<string, unknown> =
        type === "static_headers" ? { headers: parseHeaders(values.headers ?? "") } : { ...values };
      await api.saveCredential(profile.id, {
        name: values.name || spec.label,
        credential_type: type,
        secret,
      });
      // The value is gone from this browser the moment it is saved; there is no
      // endpoint that would give it back.
      setValues({});
      setShowForm(false);
      onNotice("Credential saved. It is encrypted and will not be shown again.");
      await onChanged();
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : "Could not save the credential.");
    } finally {
      setBusy(false);
    }
  }

  async function test() {
    setBusy(true);
    setTesting(true);
    try {
      const result = await api.testCredential(profile.id);
      if (result.ok) onNotice(result.message);
      else onError(result.message);
      await onChanged();
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : "Could not test the credential.");
    } finally {
      setBusy(false);
      setTesting(false);
    }
  }

  return (
    <Card
      title="Credential"
      description={profile.credential ? "Encrypted at rest and never shown again." : undefined}
    >
      <div className="flex flex-col gap-md">
        {profile.credential ? (
          <>
            <div className="grid grid-cols-1 gap-md fold:grid-cols-2">
              <KeyValue label="Type">{profile.credential.credential_type.replace(/_/g, " ")}</KeyValue>
              <KeyValue label="Name">{profile.credential.name}</KeyValue>
              <KeyValue label="Last worked">
                {profile.credential.last_succeeded_at ? (
                  formatRelative(profile.credential.last_succeeded_at)
                ) : (
                  <Muted>Never yet</Muted>
                )}
              </KeyValue>
              <KeyValue label="Last set by">
                {profile.credential.updated_by ?? <Muted>Unattributed</Muted>}
              </KeyValue>
            </div>
            <div className="flex flex-wrap items-center gap-xs">
              {/* Says which state it is in rather than only greying out. A
                  `login_form` test drives a real browser through a sign-in and
                  can run for many seconds; a control that merely goes dim for
                  that long reads as a broken button, not a working one. */}
              <Button
                size="sm"
                onClick={() => void test()}
                disabled={busy}
                icon={testing ? <span className="spinner" aria-hidden="true" /> : undefined}
                // Wide enough for the busier of the two labels, so starting a
                // test does not reflow the row and drop "Remove" onto a line of
                // its own. Measured: 103px idle against 118px with the spinner.
                className="min-w-[7.5rem]"
              >
                {testing ? "Testing…" : "Test it now"}
              </Button>
              <Button size="sm" onClick={() => setShowForm(true)}>
                Replace
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={async () => {
                  await api.deleteCredential(profile.id);
                  await onChanged();
                }}
              >
                Remove
              </Button>
            </div>
          </>
        ) : (
          !showForm && (
            <div
              className="flex flex-col items-start gap-sm rounded-md bg-surface-soft p-md"
            >
              <div className="flex items-center gap-xs">
                <KeyIcon size={16} className="text-muted" />
                <Muted>This source is fetched without a Credential Set.</Muted>
              </div>
              <Muted>Add one if it sits behind a login.</Muted>
              <Button size="sm" onClick={() => setShowForm(true)}>
                Add a credential
              </Button>
            </div>
          )
        )}

        {showForm && (
          <div
            className="border-t border-hairline-soft pt-md"
          >
            <div className="flex flex-col gap-md">
              <Field label="Type">
                <Select value={type} onChange={(e) => setType(e.target.value as CredentialType)}>
                  {CREDENTIAL_TYPES.map((entry) => (
                    <option key={entry.value} value={entry.value}>
                      {entry.label}
                    </option>
                  ))}
                </Select>
              </Field>
              {spec.hint && <Muted>{spec.hint}</Muted>}
              {/* The portal's key and a key of this profile's own, offered as
                  one choice rather than as two screens. A configured key is a
                  default, not a lock: one portal holds one account's key, and
                  a second subscription, a rotation, or one profile that should
                  read a different account are all ordinary. The server settles
                  precedence — a posted secret beats `from_template` — so this
                  only decides which one is sent. */}
              {canUsePortalKey && (
                <Field
                  label="Which key"
                  hint="The portal's key is read from its own configuration and is never shown here."
                >
                  <Select
                    value={usePortalKey ? "portal" : "own"}
                    onChange={(e) =>
                      setKeyChoice(e.target.value === "portal" ? "portal" : "own")
                    }
                  >
                    <option value="portal">
                      Use the API key configured on this portal
                    </option>
                    <option value="own">Use a different key for this source</option>
                  </Select>
                </Field>
              )}
              <Field label="Label" hint="What this is, so a failure can name it.">
                <TextInput
                  value={values.name ?? ""}
                  placeholder={canUsePortalKey && usePortalKey ? template?.publisher : spec.label}
                  onChange={(e) => setValues({ ...values, name: e.target.value })}
                />
              </Field>
              {/* Nothing to type where the portal supplies the secret. */}
              {(canUsePortalKey && usePortalKey ? [] : spec.fields).map((fieldName) =>
                fieldName === "headers" ? (
                  <Field key={fieldName} label="Headers" hint="One per line, as Name: value.">
                    <TextArea
                      value={values.headers ?? ""}
                      placeholder={"X-Api-Key: …"}
                      onChange={(e) => setValues({ ...values, headers: e.target.value })}
                    />
                  </Field>
                ) : (
                  <Field
                    key={fieldName}
                    label={CREDENTIAL_FIELD_LABELS[fieldName] ?? capitalise(fieldName)}
                  >
                    <TextInput
                      type={
                        fieldName === "password"
                          ? "password"
                          : fieldName === "login_url"
                            ? "url"
                            : "text"
                      }
                      placeholder={fieldName === "login_url" ? "https://…/login" : undefined}
                      value={values[fieldName] ?? ""}
                      onChange={(e) => setValues({ ...values, [fieldName]: e.target.value })}
                    />
                  </Field>
                ),
              )}
              <div className="flex items-center gap-xs">
                <Button variant="primary" onClick={() => void save()} disabled={busy}>
                  Save credential
                </Button>
                <Button variant="ghost" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function parseHeaders(text: string): Record<string, string> {
  const headers: Record<string, string> = {};
  for (const line of text.split("\n")) {
    const index = line.indexOf(":");
    if (index > 0) headers[line.slice(0, index).trim()] = line.slice(index + 1).trim();
  }
  return headers;
}

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** The human message an API rejection carries, or a fallback for anything else. */
function messageFor(error: unknown, fallback: string): string | null {
  if (!error) return null;
  return error instanceof ApiError ? error.message : fallback;
}
